import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, PillBottle, Search, AlertTriangle, Mail, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { ExportButton } from "../components/ExportButton";
import { AvvisoOSS } from "../components/AvvisoOSS";
import { useSegnalazioni, SegnalazioneButton, SegnalazioniBadge } from "../components/Segnalazioni";
import { ConfirmUndoDialog } from "../components/ConfirmUndoDialog";
import { MeseGiorniEsplosi, RecapOperatoriMese, RecapGiorno, AllertaBox } from "../components/MeseGiorniEsplosi";
import { UscitiInfoBox, isFuori } from "../components/UscitiInfoBox";
import { EsBadge } from "../components/EsBadge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const fmtLong = (iso) => { try { const d = new Date(iso); const s = d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return iso; } };

export default function ControlloTerapie() {
  const { selectedStructure, can } = useAuth();
  const { activeOperatore, opName, canSign, signerName, mustUnlock } = useFirma();
  const canUndo = (op) => (!!op && op === signerName);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmUndo, setConfirmUndo] = useState(null);
  const today = ymd(new Date());
  const [openDay, setOpenDay] = useState(today);
  const [mese, setMese] = useState(today.slice(0, 7));
  const giorno = openDay || today;
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("giorno");
  const monthStart = today.slice(0, 8) + "01";
  const [stoDa, setStoDa] = useState(monthStart);
  const [stoA, setStoA] = useState(today);
  const [expanded, setExpanded] = useState({});
  const [showDimessi, setShowDimessi] = useState(false);
  const [noteDraft, setNoteDraft] = useState({});

  const { data: meseData } = useQuery({
    enabled: mode === "giorno",
    queryKey: ["controllo-terapie-mese", selectedStructure, mese],
    queryFn: async () => (await api.get("/controllo-terapie/mese", { params: { structure_id: selectedStructure, mese } })).data,
  });
  const { data: ctAllerte = [] } = useQuery({
    enabled: mode === "giorno",
    queryKey: ["ct-allerte", selectedStructure],
    queryFn: async () => (await api.get("/controllo-terapie/allerte", { params: { structure_id: selectedStructure, giorni: 2 } })).data,
  });
  const ctAllertaBox = (
    <AllertaBox tone="bad" title="Terapie non prese da 2+ giorni di fila" icon={AlertTriangle}
      items={ctAllerte} testid="ct-allerta-box" onItemClick={(it) => navigate(`/ospiti/${it.ospite_id}?tab=controllo_terapie`)}
      renderItem={(it) => (<span><b>{it.cognome} {it.nome}</b> <span className="ml-1 text-red-700 font-bold">{it.giorni}g di fila</span></span>)} />
  );

  const { data: storico = [] } = useQuery({
    enabled: mode === "storico",
    queryKey: ["controllo-terapie-storico", selectedStructure, stoDa, stoA],
    queryFn: async () => (await api.get("/controllo-terapie/storico", { params: { structure_id: selectedStructure, da: stoDa, a: stoA } })).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["controllo-terapie", selectedStructure, giorno],
    queryFn: async () => (await api.get("/controllo-terapie/grid", { params: { structure_id: selectedStructure, data: giorno } })).data,
  });

  const rowsAll = data?.rows || [];
  const records = data?.records || {};
  const { byKey: segByKey, aperte: segAperte, refetch: segRefetch } = useSegnalazioni("controllo_terapie", selectedStructure, giorno);

  const save = async (r, stato, farmaci_rifiutati, note) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    const rec = records[r.ospite_id] || {};
    if (rec.stato && !canUndo(rec.operatore)) { toast.error("Puoi modificare solo le tue registrazioni. Per quella di un altro operatore usa il pulsante ! (segnalazione)."); return; }
    const payload = {
      ospite_id: r.ospite_id, structure_id: r.structure_id, data: giorno, stato, operatore: signerName,
      farmaci_rifiutati: stato === "rifiutata" ? (farmaci_rifiutati ?? rec.farmaci_rifiutati ?? []) : [],
      note: stato === "rifiutata" ? (note ?? rec.note ?? "") : "",
    };
    try {
      await api.post("/controllo-terapie/register", payload);
      qc.invalidateQueries({ queryKey: ["controllo-terapie"] });
      qc.invalidateQueries({ queryKey: ["controllo-terapie-mese"] });
      if (stato === "rifiutata" && (farmaci_rifiutati === undefined)) toast.warning("Segnalato: terapia non assunta");
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const toggleFarmaco = (r, farmaco) => {
    const rec = records[r.ospite_id] || {};
    const cur = rec.farmaci_rifiutati || [];
    const next = cur.includes(farmaco) ? cur.filter((x) => x !== farmaco) : [...cur, farmaco];
    save(r, "rifiutata", next, rec.note);
  };
  const onPresa = (r) => {
    const rec = records[r.ospite_id] || {};
    // Predefinito: terapia assunta -> tasto verde non cliccabile; attivo solo per annullare una segnalazione.
    if (rec.stato !== "rifiutata") return;
    if (!canUndo(rec.operatore)) { toast.error("Puoi modificare solo le tue registrazioni. Per quella di un altro operatore usa il pulsante ! (segnalazione)."); return; }
    setConfirmUndo({ nome: `${r.cognome} ${r.nome}`, extra: "terapia presa", action: () => { save(r, ""); setExpanded((p) => ({ ...p, [r.ospite_id]: false })); } });
  };
  const onNonPresa = (r) => {
    const rec = records[r.ospite_id] || {};
    if (rec.stato && !canUndo(rec.operatore)) { toast.error("Puoi modificare solo le tue registrazioni. Per quella di un altro operatore usa il pulsante ! (segnalazione)."); return; }
    if (rec.stato === "rifiutata") { setExpanded((p) => ({ ...p, [r.ospite_id]: true })); setNoteDraft((p) => ({ ...p, [r.ospite_id]: rec.note || "" })); return; }
    save(r, "rifiutata");
    setExpanded((p) => ({ ...p, [r.ospite_id]: true }));
    setNoteDraft((p) => ({ ...p, [r.ospite_id]: "" }));
  };
  const onSalva = (r) => {
    const rec = records[r.ospite_id] || {};
    const note = noteDraft[r.ospite_id] ?? rec.note ?? "";
    save(r, "rifiutata", rec.farmaci_rifiutati || [], note);
    setExpanded((p) => ({ ...p, [r.ospite_id]: false }));
    toast.success("Segnalazione salvata");
  };

  const q = search.trim().toLowerCase();
  const isDimessoR = (r) => { const d = r.data_dimissione ? String(r.data_dimissione).trim().slice(0, 10) : ""; return !!d && d <= ymd(new Date()); };
  const rows = rowsAll.filter((r) => !q || `${r.cognome} ${r.nome}`.toLowerCase().includes(q));
  const rifiutiCount = rowsAll.filter((r) => records[r.ospite_id]?.stato === "rifiutata").length;
  const ctOpCounts = {};
  Object.values(records).forEach((rec) => { if (rec?.stato) { const n = rec.operatore || "—"; ctOpCounts[n] = (ctOpCounts[n] || 0) + 1; } });
  const ctOperatoriGiorno = Object.entries(ctOpCounts).map(([nome, count]) => ({ nome, count })).sort((a, b) => b.count - a.count);
  const ctRegistrate = Object.values(records).filter((r) => r?.stato).length;
  const ctStats = [
    { key: "non-prese", label: "Non prese", value: rifiutiCount, tone: rifiutiCount > 0 ? "bad" : "ok" },
    { key: "firmate", label: "Controlli firmati", value: ctRegistrate, tone: "muted" },
    { key: "ospiti", label: "Ospiti", value: rowsAll.filter((r) => !isDimessoR(r) && !["ospedale", "uscita"].includes(r.presenza)).length, tone: "muted" },
  ];

  const exportRows = rowsAll.map((r) => { const rec = records[r.ospite_id] || {}; return { ...r, _stato: rec.stato === "rifiutata" ? "NON assunta" : "Assunta / OK", _far: (rec.farmaci_rifiutati || []).join(", "), _note: rec.note || "" }; });
  const cols = [
    { header: "Ospite", accessor: (r) => `${r.cognome} ${r.nome}` },
    { header: "Camera/zona", accessor: (r) => r.camera || "" },
    { header: "Esito", accessor: (r) => r._stato },
    { header: "Medicine non prese", accessor: (r) => r._far },
    { header: "Nota", accessor: (r) => r._note },
  ];
  const stoCols = [
    { header: "Data", accessor: (r) => r.data },
    { header: "Ospite", accessor: (r) => r.ospite_nome },
    { header: "Camera/zona", accessor: (r) => r.camera || "" },
    { header: "Medicine non prese", accessor: (r) => (r.farmaci_rifiutati || []).join(", ") },
    { header: "Nota", accessor: (r) => r.note || "" },
    { header: "Registrato da", accessor: (r) => r.updated_by || "" },
  ];
  const qs = search.trim().toLowerCase();
  const storicoF = storico.filter((r) => !qs || (r.ospite_nome || "").toLowerCase().includes(qs));

  const [mailOpen, setMailOpen] = useState(false);
  const [mail, setMail] = useState({ periodo: "settimana", da: "", a: "", email: "", auto: false });
  const [mailBusy, setMailBusy] = useState(false);
  const [inviiOpen, setInviiOpen] = useState(false);
  const [invii, setInvii] = useState([]);
  const openInvii = async () => {
    if (selectedStructure === "all") { toast.error("Seleziona una struttura specifica"); return; }
    try { const r = await api.get("/controllo-terapie/invii", { params: { structure_id: selectedStructure } }); setInvii(r.data); } catch { setInvii([]); }
    setInviiOpen(true);
  };
  const openMail = async () => {
    if (selectedStructure === "all") { toast.error("Seleziona una struttura specifica"); return; }
    const d = new Date(); const a = ymd(d); const s = new Date(); s.setDate(s.getDate() - 6);
    let email = "", auto = false;
    try { const r = await api.get("/controllo-terapie/impostazioni-medico", { params: { structure_id: selectedStructure } }); email = r.data.email_medico || ""; auto = !!r.data.invio_auto_medico; } catch {}
    setMail({ periodo: "settimana", da: ymd(s), a, email, auto });
    setMailOpen(true);
  };
  const rangeFor = (periodo) => {
    const a = new Date();
    if (periodo === "mese") { const s = new Date(a.getFullYear(), a.getMonth(), 1); return [ymd(s), ymd(a)]; }
    if (periodo === "settimana") { const s = new Date(a); s.setDate(s.getDate() - 6); return [ymd(s), ymd(a)]; }
    return [mail.da, mail.a];
  };
  const inviaMail = async () => {
    const [da, a] = rangeFor(mail.periodo);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail.email.trim())) { toast.error("Inserisci un'email valida"); return; }
    if (!da || !a) { toast.error("Indica il periodo"); return; }
    setMailBusy(true);
    try {
      const r = await api.post("/controllo-terapie/invia-medico", { structure_id: selectedStructure, da, a, email: mail.email.trim(), salva_auto: mail.auto });
      toast.success(`Email inviata al medico · ${r.data.ospiti} ospiti, ${r.data.segnalazioni} segnalazioni`);
      setMailOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    setMailBusy(false);
  };

  return (
    <div data-testid="controllo-terapie-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><PillBottle size={22} className="text-primary" /> Controllo terapie</h1>
          <p className="text-sm text-muted-foreground">Controlla che ogni ospite assuma la terapia. Segnala solo chi <b>non</b> l'ha presa (e quali medicine).</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden text-xs font-semibold mr-1" data-testid="ct-mode">
            <button onClick={() => setMode("giorno")} data-testid="ct-mode-giorno" className={`px-3 py-2 transition-colors ${mode === "giorno" ? "bg-primary text-white" : "hover:bg-secondary"}`}>Giornaliero</button>
            <button onClick={() => setMode("storico")} data-testid="ct-mode-storico" className={`px-3 py-2 transition-colors ${mode === "storico" ? "bg-primary text-white" : "hover:bg-secondary"}`}>Storico</button>
          </div>
          <Button variant="outline" size="sm" onClick={openMail} data-testid="ct-invia-medico-btn"><Mail size={15} className="mr-1" /> Invia al medico</Button>
          <Button variant="ghost" size="sm" onClick={openInvii} data-testid="ct-invii-btn" title="Storico invii email">Storico invii</Button>
          {mode === "giorno" ? (
            <ExportButton filename="controllo-terapie" title="Controllo terapie" subtitle={fmtLong(giorno)} columns={cols} rows={exportRows} color="#1f7a5c" testid="ct-export" />
          ) : (
            <ExportButton filename="storico-terapie-non-prese" title="Storico terapie non assunte" subtitle={`${stoDa} → ${stoA}`} columns={stoCols} rows={storico} color="#dc2626" testid="ct-sto-export" />
          )}
        </div>
      </div>
      <AvvisoOSS className="mb-4" />

      {mode === "giorno" && (
      <MeseGiorniEsplosi embedded color="#1f7a5c"
        mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
        counts={meseData?.counts || {}} countLabel="controlli"
        monthTop={<>{ctAllertaBox}<RecapOperatoriMese operatori={meseData?.operatori || []} color="#1f7a5c" label="controlli" icon={PillBottle} /></>}>
      {ctAllertaBox}
      <RecapGiorno operatori={ctOperatoriGiorno} stats={ctStats} color="#1f7a5c" opLabel="controlli" />
      <UscitiInfoBox usciti={rowsAll.filter(isFuori)} testid="ct-usciti-box" />
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold ${rifiutiCount > 0 ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`} data-testid="ct-count">
          {rifiutiCount > 0 ? <><AlertTriangle size={15} /> {rifiutiCount} da segnalare</> : <><Check size={15} /> Nessuna segnalazione</>}
        </div>
        <SegnalazioniBadge categoria="controllo_terapie" aperte={segAperte} />
        {mustUnlock && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1.5 text-sm font-semibold"><AlertTriangle size={15} /> Sblocca con la Firma Rapida</span>}
        <div className="relative ml-auto">
          <Search size={16} className="absolute left-3 top-2.5 text-muted-foreground" />
          <Input placeholder="Cerca ospite..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" data-testid="ct-search" />
        </div>
      </div>

      {isLoading ? (
        <div className="gr-card p-10 text-center text-muted-foreground">Caricamento…</div>
      ) : rows.length === 0 ? (
        <div className="gr-card p-10 text-center text-muted-foreground" data-testid="ct-empty">Nessun ospite presente</div>
      ) : (
        <div className="space-y-2">
          {(() => {
          const isDimesso = (r) => { const d = r.data_dimissione ? String(r.data_dimissione).trim().slice(0, 10) : ""; return !!d && d <= ymd(new Date()); };
          const attivi = rows.filter((r) => !isDimesso(r));
          const dimessi = rows.filter(isDimesso);
          const renderRow = (r, num) => {
            const rec = records[r.ospite_id] || {};
            const rifiutata = rec.stato === "rifiutata";
            const rifiutati = rec.farmaci_rifiutati || [];
            const dim = isDimesso(r);
            const fuori = ["ospedale", "uscita"].includes(r.presenza);
            const fuoriDal = r.fuori_dal || null;
            const fuoriOggi = r.fuori_giorno ?? (fuori && (!fuoriDal || giorno >= fuoriDal));
            const fuoriLbl = r.presenza === "ospedale" ? "Ricoverato" : "Uscito";
            const fuoriDalLbl = fuoriDal ? ` dal ${fuoriDal.slice(8, 10)}/${fuoriDal.slice(5, 7)}` : "";
            return (
              <div key={r.ospite_id} className={`rounded-xl border p-3 transition-colors ${fuoriOggi ? "bg-amber-50/60 border-amber-200" : rifiutata ? "border-red-300 bg-red-50" : fuori ? "bg-amber-50/40" : "bg-white"}`} data-testid={`ct-row-${r.ospite_id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold sm:truncate flex items-center gap-2"><span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold tabnum">{num != null ? num : "–"}</span><span className="min-w-0 sm:truncate"><button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/ospiti/${r.ospite_id}?tab=controllo_terapie`); }} className="hover:underline text-left" data-testid={`ct-nome-${r.ospite_id}`}>{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id} />{r.camera && <span className="text-xs text-muted-foreground font-normal"> · Camera/zona {r.camera}</span>}</span>{dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">Dimesso</span>}{fuori && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${r.presenza === "ospedale" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`ct-fuori-badge-${r.ospite_id}`}>{fuoriLbl}{fuoriDalLbl}</span>}</div>
                    {fuoriOggi ? (
                      <div className="text-[11px] text-amber-700 font-medium mt-0.5" data-testid={`ct-fuori-note-${r.ospite_id}`}>Fuori struttura — non conteggiato</div>
                    ) : rifiutata ? (
                      <div className="mt-1 flex flex-wrap items-center gap-1.5" data-testid={`ct-summary-${r.ospite_id}`}>
                        {rifiutati.length ? rifiutati.map((f) => <span key={f} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700" data-testid={`ct-summary-farmaco-${r.ospite_id}-${f}`}>{f}</span>) : <span className="text-[11px] font-semibold text-red-700">Tutte le medicine</span>}
                        {rec.note && <span className="text-[11px] text-red-800 italic" data-testid={`ct-summary-note-${r.ospite_id}`}>"{rec.note}"</span>}
                        {rec.updated_by && <span className="text-[11px] text-red-700">· segnalato da {rec.updated_by}</span>}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">{r.farmaci.length ? `${r.farmaci.length} terapie quotidiane` : "Nessuna terapia quotidiana"}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                    <button onClick={() => onPresa(r)} disabled={fuoriOggi || !rifiutata} title={fuoriOggi ? "Ospite fuori struttura" : (!rifiutata ? "Predefinito: terapia presa" : "Annulla segnalazione")} className={`h-11 sm:h-9 flex-1 sm:flex-none px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : !rifiutata ? "bg-green-600 text-white cursor-default" : "bg-secondary hover:bg-green-100"}`} data-testid={`ct-ok-${r.ospite_id}`}><Check size={16} /> Presa</button>
                    <button onClick={() => onNonPresa(r)} disabled={fuoriOggi} title={fuoriOggi ? "Ospite fuori struttura" : ""} className={`h-11 sm:h-9 flex-1 sm:flex-none px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : rifiutata ? "bg-red-600 text-white" : "bg-secondary hover:bg-red-100"}`} data-testid={`ct-no-${r.ospite_id}`}><X size={16} /> Non presa</button>
                    <SegnalazioneButton categoria="controllo_terapie" structureId={selectedStructure} data={giorno}
                      ospiteId={r.ospite_id} ospiteNome={`${r.cognome} ${r.nome}`}
                      entry={segByKey[`${r.ospite_id}_${giorno}`]} onChanged={segRefetch} />
                  </div>
                </div>
                {rifiutata && (expanded[r.ospite_id] ? (
                  <div className="mt-3 pt-3 border-t border-red-200" data-testid={`ct-detail-${r.ospite_id}`}>
                    {r.farmaci.length > 0 ? (
                      <>
                        <div className="text-xs font-semibold text-red-700 mb-2">Quali medicine non ha preso? (opzionale)</div>
                        <div className="flex flex-wrap gap-2">
                          {r.farmaci.map((f) => {
                            const sel = rifiutati.includes(f.farmaco);
                            return (
                              <button key={f.id} onClick={() => toggleFarmaco(r, f.farmaco)} data-testid={`ct-farmaco-${r.ospite_id}-${f.id}`}
                                className={`min-h-[44px] px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${sel ? "bg-red-600 text-white border-red-600" : "bg-white hover:bg-red-50 border-red-200 text-red-900"}`}>
                                {f.evento && <span className={`mr-1 text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 ${sel ? "bg-white/25" : "bg-rose-100 text-rose-700"}`}>Evento</span>}{f.farmaco}{f.dosaggio ? ` · ${f.dosaggio}` : ""}{f.orari ? <span className="opacity-70"> ({f.orari})</span> : ""}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : <div className="text-xs text-muted-foreground">Nessuna terapia quotidiana registrata per questo ospite.</div>}
                    <Input placeholder="Nota (facoltativa)" value={noteDraft[r.ospite_id] ?? rec.note ?? ""} onChange={(e) => setNoteDraft((p) => ({ ...p, [r.ospite_id]: e.target.value }))} className="mt-2 w-full h-11 text-sm" data-testid={`ct-note-${r.ospite_id}`} />
                    <div className="mt-2 flex justify-end">
                      <Button onClick={() => onSalva(r)} className="h-10 bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid={`ct-salva-${r.ospite_id}`}><Check size={16} className="mr-1.5" /> Salva</Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex justify-end">
                    <button onClick={() => { setExpanded((p) => ({ ...p, [r.ospite_id]: true })); setNoteDraft((p) => ({ ...p, [r.ospite_id]: rec.note || "" })); }} className="text-xs font-semibold text-red-700 hover:underline" data-testid={`ct-modifica-${r.ospite_id}`}>Modifica medicine / nota</button>
                  </div>
                ))}
              </div>
            );
          };
          return (
            <>
              {attivi.map((r, i) => renderRow(r, i + 1))}
              {dimessi.length > 0 && (
                <div data-testid="ct-dimessi-section">
                  <button onClick={() => setShowDimessi((v) => !v)} data-testid="ct-toggle-dimessi" className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg bg-orange-50 border border-orange-200 mt-2">
                    <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
                    <span className="font-bold text-sm text-orange-800">Dimessi</span>
                    <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dimessi.length}</span>
                  </button>
                  {showDimessi && <div className="space-y-2 mt-2">{dimessi.map((r) => renderRow(r, null))}</div>}
                </div>
              )}
            </>
          );
          })()}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">Di default la terapia è considerata assunta. Tocca <b>Non presa</b> per segnalare chi non l'ha presa; le medicine mostrate provengono dalla scheda <b>Terapie</b> dell'ospite. Ogni segnalazione genera una notifica.</p>
      </MeseGiorniEsplosi>
      )}

      {mode === "storico" && (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="ct-storico-controls">
            <span className="text-xs font-semibold text-muted-foreground">Dal</span>
            <Input type="date" value={stoDa} max={stoA} onChange={(e) => setStoDa(e.target.value)} className="w-[150px]" data-testid="ct-sto-da" />
            <span className="text-xs font-semibold text-muted-foreground">al</span>
            <Input type="date" value={stoA} min={stoDa} onChange={(e) => setStoA(e.target.value)} className="w-[150px]" data-testid="ct-sto-a" />
            <div className="rounded-lg border px-3 py-1.5 text-sm font-semibold bg-red-50 border-red-200 text-red-700" data-testid="ct-sto-count">{storicoF.length} segnalazioni</div>
            <div className="relative ml-auto">
              <Search size={16} className="absolute left-3 top-2.5 text-muted-foreground" />
              <Input placeholder="Cerca ospite..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" data-testid="ct-sto-search" />
            </div>
          </div>
          <div className="gr-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/60">
                <th className="text-left p-3 font-bold">Data</th>
                <th className="text-left p-3 font-bold">Ospite</th>
                <th className="text-left p-3 font-bold">Medicine non prese</th>
                <th className="text-left p-3 font-bold">Nota</th>
                <th className="text-left p-3 font-bold">Registrato da</th>
              </tr></thead>
              <tbody>
                {storicoF.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground" data-testid="ct-sto-empty">Nessuna terapia non assunta nel periodo</td></tr>}
                {storicoF.map((r, i) => (
                  <tr key={i} className="border-t" data-testid={`ct-sto-row-${i}`}>
                    <td className="p-3 tabnum whitespace-nowrap">{fmtLong(r.data)}</td>
                    <td className="p-3 font-semibold"><button type="button" onClick={() => navigate(`/ospiti/${r.ospite_id}?tab=controllo_terapie`)} className="hover:underline text-left" data-testid={`ct-sto-nome-${i}`}>{r.ospite_nome}</button><EsBadge id={r.ospite_id} nome={r.ospite_nome} />{r.camera ? <span className="text-xs text-muted-foreground font-normal"> · Cam {r.camera}</span> : ""}</td>
                    <td className="p-3">{(r.farmaci_rifiutati || []).length ? <div className="flex flex-wrap gap-1">{r.farmaci_rifiutati.map((f, j) => <span key={j} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{f}</span>)}</div> : <span className="text-xs text-muted-foreground">Tutte / non specificato</span>}</td>
                    <td className="p-3 text-muted-foreground">{r.note || "—"}</td>
                    <td className="p-3 text-xs text-muted-foreground">{r.updated_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-md" data-testid="ct-mail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} /> Invia riepilogo al medico</DialogTitle>
            <DialogDescription>Invia via email l'elenco delle terapie non assunte per il periodo scelto. Vengono inclusi solo gli ospiti con almeno un rifiuto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Periodo</Label>
              <div className="flex rounded-lg border overflow-hidden text-sm font-semibold">
                {[["settimana", "Ultima settimana"], ["mese", "Questo mese"], ["custom", "Da → a"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setMail((p) => ({ ...p, periodo: v }))} data-testid={`ct-mail-periodo-${v}`} className={`flex-1 px-2 py-1.5 transition-colors ${mail.periodo === v ? "bg-primary text-white" : "hover:bg-secondary"}`}>{l}</button>
                ))}
              </div>
            </div>
            {mail.periodo === "custom" && (
              <div className="grid grid-cols-2 gap-2" data-testid="ct-mail-custom">
                <div><Label className="text-xs font-semibold mb-1 block">Dal</Label><Input type="date" value={mail.da} max={mail.a} onChange={(e) => setMail((p) => ({ ...p, da: e.target.value }))} data-testid="ct-mail-da" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Al</Label><Input type="date" value={mail.a} min={mail.da} onChange={(e) => setMail((p) => ({ ...p, a: e.target.value }))} data-testid="ct-mail-a" /></div>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold mb-1 block">Email dei destinatari</Label>
              <Input type="text" value={mail.email} onChange={(e) => setMail((p) => ({ ...p, email: e.target.value }))} placeholder="medico@esempio.it, coordinatore@esempio.it" data-testid="ct-mail-email" />
              <p className="text-[11px] text-muted-foreground mt-1">Puoi inserire più indirizzi separati da virgola.</p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer font-medium" data-testid="ct-mail-auto">
              <Checkbox checked={mail.auto} onCheckedChange={(v) => setMail((p) => ({ ...p, auto: !!v }))} /> Invia automaticamente ogni lunedì il riepilogo settimanale a questa email
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMailOpen(false)}>Annulla</Button>
            <Button onClick={inviaMail} disabled={mailBusy} className="font-semibold" data-testid="ct-mail-send"><Mail size={15} className="mr-1" /> {mailBusy ? "Invio…" : "Invia email"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviiOpen} onOpenChange={setInviiOpen}>
        <DialogContent className="max-w-2xl" data-testid="ct-invii-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} /> Storico invii al medico</DialogTitle>
            <DialogDescription>Elenco dei riepiloghi inviati, con destinatari, periodo ed esito.</DialogDescription>
          </DialogHeader>
          {invii.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="ct-invii-empty">Nessun invio registrato.</p>
          ) : (
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/60 text-left">
                  <th className="p-2 font-bold">Quando</th>
                  <th className="p-2 font-bold">Destinatari</th>
                  <th className="p-2 font-bold">Periodo</th>
                  <th className="p-2 font-bold">Ospiti</th>
                  <th className="p-2 font-bold">Tipo</th>
                  <th className="p-2 font-bold">Esito</th>
                </tr></thead>
                <tbody>
                  {invii.map((r) => (
                    <tr key={r.id} className="border-t align-top" data-testid={`ct-invio-${r.id}`}>
                      <td className="p-2 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}<div className="text-[11px] text-muted-foreground">{r.inviato_da}</div></td>
                      <td className="p-2">{(r.destinatari || []).map((e, i) => <div key={i} className="truncate max-w-[180px]">{e}</div>)}</td>
                      <td className="p-2 whitespace-nowrap text-xs">{r.da} → {r.a}</td>
                      <td className="p-2 tabnum">{r.ospiti}</td>
                      <td className="p-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.tipo === "automatico" ? "bg-blue-100 text-blue-700" : "bg-secondary text-foreground"}`}>{r.tipo}</span></td>
                      <td className="p-2"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.esito === "inviata" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.esito}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setInviiOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmUndoDialog ctx={confirmUndo} onClose={() => setConfirmUndo(null)} />
    </div>
  );
}
